import {
  Component,
  Inject,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { criteria } from '@app/library/models/criterias.model';
import { PlaylistApi } from '@app/library/models/playlist-api.model';
import { Playlist } from '@app/library/models/playlist.model';
import {
  CreatePlaylistDynamicRequest,
  CreatePlaylistDynamicSuccess,
  CreatePlaylistManualFailure,
  CreatePlaylistManualRequest,
  CreatePlaylistManualSuccess,
  EditPlaylistDynamicRequest,
  EditPlaylistDynamicSuccess,
  EditPlaylistManualRequest,
  EditPlaylistManualSuccess,
  InvalidCriteria,
} from '@app/library/states/playlists.actions';
import { PlaylistsState } from '@app/library/states/playlists.state';
import { emptyCriterionValidator } from '@app/shared/custom-validators';
import { isEqualFormData } from '@app/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import {
  ErrorFormFormat,
  FormHelperService,
  MobileSidenavService,
  UiStoreHelperService,
} from '@radioking/shared/common-services';
import { DynamicEnabledPanel } from '@radioking/shared/pannel-manager';
import { SugestionInput } from '@radioking/ui/input';
import { InputColorPickerComponent } from '@radioking/ui/input-color-picker';
import amplitude from 'amplitude-js';
import { cloneDeep } from 'lodash';
import { Observable, Subject, Subscription } from 'rxjs';
import {
  getCriteriaTextSuggestion,
  getCriteriaTitle,
  getCriteriaType,
} from '../library-create-playlist/library-create-playlist.component';

@Component({
  selector: 'rk-mob-create-playlist',
  templateUrl: './mob-create-playlist.component.html',
  styleUrls: ['./mob-create-playlist.component.scss'],
})
export class MobCreatePlaylistComponent implements OnInit, DynamicEnabledPanel {
  createPlaylistForm: UntypedFormGroup;
  createPlaylistFormErrors: ErrorFormFormat;
  initialFormData: Partial<Playlist> = {};

  isSaving$: Observable<boolean>;

  submitSubject: Subject<void> = new Subject<void>();

  @ViewChild('stepper')
  stepper: MatStepper;

  $step: WritableSignal<number> = signal(1);
  $totalSteps: WritableSignal<number> = signal(1);
  $isEditing: WritableSignal<boolean> = signal(false);

  subscription = new Subscription();

  constructor(
    private readonly formHelper: FormHelperService,
    private readonly fb: UntypedFormBuilder,
    private readonly helper: UiStoreHelperService,
    private readonly translateService: TranslateService,
    private readonly store: Store,
    private readonly actions$: Actions,
    public readonly dialogRef: MatDialogRef<MobCreatePlaylistComponent>,
    private readonly mobileService: MobileSidenavService,
    @Inject(MAT_DIALOG_DATA) public data: { playlist: boolean } | null,
  ) {
    this.$isEditing.set(!!this.data);
  }

  ngOnInit() {
    amplitude.getInstance().logEvent('add playlist form');
    if (this.$isEditing()) {
      this.createForm(this.store.selectSnapshot(PlaylistsState.editingPlaylist));
    } else {
      this.createForm();
    }

    this.isSaving$ = this.helper.getLoadingState(
      [CreatePlaylistManualRequest],
      [CreatePlaylistManualSuccess, CreatePlaylistManualFailure],
    );
    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(
            CreatePlaylistManualSuccess,
            CreatePlaylistDynamicSuccess,
            EditPlaylistManualSuccess,
            EditPlaylistDynamicSuccess,
          ),
        )
        .subscribe(() => {
          this.dialogRef.close();
          this.mobileService.close();
        }),
    );
  }

  hasToShowConfirmOnExit(): boolean {
    return false;
  }

  isDirty(): boolean {
    return (
      this.createPlaylistForm &&
      !this.createPlaylistForm.pristine &&
      !isEqualFormData(this.createPlaylistForm.getRawValue(), this.initialFormData)
    );
  }

  get criteria() {
    return this.createPlaylistForm.get('criteria') as UntypedFormArray;
  }

  createForm(playlist: Playlist = null) {
    if (playlist && playlist.type === 'dynamic') {
      this.$totalSteps.set(2);
    }
    this.createPlaylistFormErrors = {
      name: {},
      color: {},
      type: {},
    };
    this.createPlaylistForm = this.fb.group({
      name: [playlist ? playlist.name : '', Validators.required],
      color: [
        playlist ? playlist.color : InputColorPickerComponent.getRandomColor(),
        Validators.required,
      ],
      type: playlist ? playlist.type : 'manual',
      criteria:
        playlist && playlist.type === 'dynamic' && playlist.criteria
          ? this.fb.array(playlist.criteria, emptyCriterionValidator())
          : this.fb.array([], emptyCriterionValidator()),
    });

    this.initialFormData = cloneDeep({ ...this.createPlaylistForm.getRawValue() });

    this.subscription.add(
      this.createPlaylistForm.valueChanges.subscribe(v => {
        this.createPlaylistForm.clearValidators();
        this.formHelper.updateErrorMapping(
          this.createPlaylistForm,
          this.createPlaylistFormErrors,
        );
        this.$totalSteps.set(v.type === 'manual' ? 1 : 2);
      }),
    );
    this.formHelper.updateErrorMapping(
      this.createPlaylistForm,
      this.createPlaylistFormErrors,
    );
  }

  addCriteria(field: string) {
    this.criteria.push(this.fb.control({ field, operator: 'equals', value: '' }));
    setTimeout(() => {
      const content: HTMLElement = document.querySelector(
        'rk-mob-create-playlist',
      ).parentElement;
      if (content) {
        content.style.scrollBehavior = 'smooth';
        content.scrollTop = 9999;
        content.style.scrollBehavior = 'auto';
      }
    }, 200);
  }

  removeCriteria(index: number) {
    this.criteria.removeAt(index);
  }

  getCriteriaType(field: string): string {
    return getCriteriaType(field);
  }

  getCriteriaTitle(field: string): string {
    return getCriteriaTitle(field, this.translateService);
  }

  getCriteriaTextSuggestion(field: criteria): SugestionInput[] {
    return getCriteriaTextSuggestion(field, this.store);
  }

  exitPanel() {
    this.dialogRef.close();
  }

  @Dispatch()
  invalidCriteria = () => new InvalidCriteria();

  submit() {
    if (this.$isEditing()) {
      if (isEqualFormData(this.createPlaylistForm.getRawValue(), this.initialFormData)) {
        this.exitPanel();

        return;
      }
      const currentPlaylist = this.store.selectSnapshot(PlaylistsState.editingPlaylist);
      if (
        currentPlaylist.type === 'dynamic' &&
        !this.createPlaylistForm.get('criteria').pristine
      ) {
        this.submitSubject.next();
        if (this.createPlaylistForm.invalid) {
          this.invalidCriteria();

          return;
        }
        this.editDynamicPlaylist(currentPlaylist.id);

        return;
      }
      this.editManualPlaylist(currentPlaylist.id);
    } else {
      if (this.createPlaylistForm.get('type').value === 'dynamic') {
        this.submitSubject.next();
        if (this.createPlaylistForm.invalid) {
          this.invalidCriteria();

          return;
        }
        this.createDynamicPlaylist();

        return;
      }
      this.createManualPlaylist();
    }
  }

  @Dispatch()
  createManualPlaylist = () => {
    const formValues = this.createPlaylistForm.value;
    const playlist: Partial<PlaylistApi> = {
      name: formValues.name,
      color: formValues.color,
      type: formValues.type,
    };

    return new CreatePlaylistManualRequest(playlist);
  };

  @Dispatch()
  createDynamicPlaylist = () => {
    const formValues = this.createPlaylistForm.value;
    const playlist: Partial<PlaylistApi> = {
      name: formValues.name,
      color: formValues.color,
      type: formValues.type,
      criteria: formValues.criteria,
    };

    return new CreatePlaylistDynamicRequest(playlist);
  };

  @Dispatch()
  editManualPlaylist = (playlistId: number) => {
    const formValues = this.createPlaylistForm.value;
    const playlist: Partial<PlaylistApi> = {
      name: formValues.name,
      color: formValues.color,
    };

    return new EditPlaylistManualRequest(playlistId, playlist);
  };

  @Dispatch()
  editDynamicPlaylist = (playlistId: number) => {
    const formValues = this.createPlaylistForm.value;
    const playlist: Partial<PlaylistApi> = {
      name: formValues.name,
      color: formValues.color,
      type: 'dynamic',
      criteria: formValues.criteria,
    };

    return new EditPlaylistDynamicRequest(playlistId, playlist);
  };

  getCriteriaLength(): number {
    return (this.createPlaylistForm.get('criteria') as UntypedFormArray).length;
  }

  nextStep() {
    this.$step.update(v => v + 1);
    this.stepper.next();
  }

  prevStep() {
    this.$step.update(v => v - 1);
    this.stepper.previous();
  }
}
